import { RefObject, useEffect, useRef } from "react";

const FOCUSABLE =
  'button:not([disabled]), summary, a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), iframe, [tabindex]:not([tabindex="-1"])';

// A TV remote's arrows move focus to the nearest control on screen (the WebView's spatial
// navigation), so they can't reach what's covered or never focused. When a layer opens (Month, a
// sheet, posters, video), focus goes into it; a modal layer (aria-modal) also makes everything
// behind it inert; closing gives focus back to the button that opened it. Touch shows none of this:
// the focus ring only appears after a key press.
export const useRemoteFocus = (shell: RefObject<HTMLElement | null>) => {
  const layer = useRef<Element | null>(null);
  const opener = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const root = shell.current;
    if (!root) return;
    const layers = root.querySelectorAll(':scope > [role="dialog"]');
    const top = layers[layers.length - 1] ?? null;
    const modal = top?.getAttribute("aria-modal") === "true";
    // Every render: a panel that appears behind an open layer (the partner player) is covered too.
    // The scrim stays live so a tap outside "Your masjids" still closes it.
    for (const child of Array.from(root.children) as HTMLElement[]) {
      child.inert = modal && child !== top && !child.classList.contains("hs-switch-scrim");
    }
    if (top === layer.current) return;
    if (!layer.current) opener.current = document.activeElement as HTMLElement | null;
    layer.current = top;
    if (top) {
      if (!top.contains(document.activeElement)) {
        top.querySelector<HTMLElement>(FOCUSABLE)?.focus({ preventScroll: true });
      }
    } else if (opener.current?.isConnected) {
      opener.current.focus({ preventScroll: true });
      opener.current = null;
    }
  });

  // Idle: let go of focus altogether, so a TV's focus ring doesn't sit on a button all day.
  return () => {
    opener.current = null;
    (document.activeElement as HTMLElement | null)?.blur();
  };
};
